import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Output,
  EventEmitter,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { ApplicationConstants } from '@constants/app.constants';
import {
  IconoirXmark,
  IconoirBell,
  IconoirDoubleCheck,
  IconoirCustomBellSolid,
} from '@ey-xd/motif-icon';
import {
  ButtonSizes,
  MotifAvatarModule,
  MotifButtonModule,
  MotifCardModule,
  MotifIconModule,
  MotifModal,
  MotifTooltipModule,
} from '@ey-xd/ng-motif';
import { TranslatePipe, TranslateDirective } from '@ngx-translate/core';
import { CommonService } from '@services/common.service';
import { NotificationService } from '@services/notification.service';
import { SecureStorageService } from '@services/securestorage.service';

@Component({
  selector: 'app-general-mention',
  imports: [
    CommonModule,
    MotifIconModule,
    MotifCardModule,
    MotifTooltipModule,
    TranslatePipe,
    TranslateDirective,
    MotifButtonModule,
    MotifAvatarModule,
  ],
  templateUrl: './general-mention.component.html',
  styleUrl: './general-mention.component.scss',
})
export class GeneralMentionComponent {
  closeIcon = IconoirXmark;
  bellIcon = IconoirBell;
  readIcon = IconoirDoubleCheck;
  solidBellIcon = IconoirCustomBellSolid;
  totalCount: number = 0;
  buttonSizes = ButtonSizes;
  @Input() notification: any;
  @Output() notificationDeleted = new EventEmitter<string>();

  constructor(
    public commonService: CommonService,
    private storageService: SecureStorageService,
    private readonly modal: MotifModal,
    private readonly router: Router,
    private notificationService: NotificationService,
    private sanitizer: DomSanitizer,
  ) {}

  get messageHtml(): string {
    const raw = this.notification?.message ?? '';
    return this.sanitizer.sanitize(SecurityContext.HTML, raw) ?? '';
  }

  ngOnInit() {
    this.notificationService.totalCount$.subscribe((count) => {
      this.totalCount = count;
    });
  }

  get commentHtml(): string {
    const raw = this.notification?.metadata?.Comment ?? '';
    return this.sanitizer.sanitize(SecurityContext.HTML, raw) ?? '';
  }

  markAsRead(notification: any) {
    if (!notification || notification.isRead) return;
    this.notificationService.markAsRead(notification.id).subscribe({
      next: () => (notification.isRead = true),
      error: (err) => console.error('Failed to mark notification as read', err),
    });
  }

  deleteNotification(notification: any) {
    if (!notification) return;
    this.notificationService
      .deleteNotification(notification.id, this.totalCount)
      .subscribe({
        next: () => this.notificationDeleted.emit(notification.id),
        error: (err) => console.error('Failed to delete notification', err),
      });
  }

  openApprovalLink(notification: any) {
    this.markAsRead(notification);
    const url = notification?.metadata?.URL;
    if (url) {
      this.modal.closeAll();
      this.router.navigate(['approval-request']);
    }
  }

  openCommentLink(notification: any) {
    this.markAsRead(notification);
    const metadata = notification?.metadata;
    // ensure modal is closed before navigating
    this.modal.closeAll();
    const projectId = metadata?.TenantId;
    const componentId = metadata?.ItemId;
    const type = metadata?.Type;
    // Prefer route segments when IDs are present
    if (projectId && componentId && type !== undefined && type !== null) {
      const storageKey = `${ApplicationConstants.openedCommentKeyPrefix}${projectId}_${componentId}`;

      if (sessionStorage.getItem(storageKey) !== null) {
        this.storageService.removeSessionStorageData(storageKey);
      }

      // Append a timestamp query param so the router treats this as a new URL
      // and performs navigation even if the path/params are identical.
      this.router.navigate(
        [
          '/project-detail',
          ApplicationConstants.PMPRO,
          projectId,
          componentId,
          String(type),
        ],
        { queryParams: { _ts: Date.now() } },
      );
      return;
    }
  }
}
